// Piezas genéricas de datos para formularios Patrón B "de campos" (016, 053,
// 052, 058...): campos simples de texto/número/fecha/hora, marca PRE/DEF de
// los diagnósticos del Anexo 1 y el formato "texto (CIE)" del PDF.
import type { DiagnosticoCie10Valor } from './SelectorDiagnosticoCie10';
import type { OpcionMarca } from './MarcaDiagnostico';
import type { Par } from './index';

export interface OpcionCampo {
  readonly valor: string;
  readonly etiqueta: string;
}

/**
 * `numeroNA` es un número que también admite "NA" (el instructivo pide NA cuando una variable no se puede llenar: un `<input type=number>` no lo permite);
 * `select` usa `opciones`; `casilla` guarda `'true'` o `''` (un casillero del papel);
 * `area` es un texto largo a ancho completo; `siNo` guarda `'si'`, `'no'` o `''` (sin responder) — ver `SiNoValor`.
 */
export interface CampoSimple {
  readonly clave: string;
  readonly etiqueta: string;
  readonly tipo?: 'texto' | 'numero' | 'numeroNA' | 'fecha' | 'hora' | 'select' | 'casilla' | 'area' | 'siNo';
  readonly opciones?: readonly OpcionCampo[];
  /**
   * Campo derivado (IMC...): se muestra de solo lectura con el valor que devuelve esta función sobre los demás valores del bloque,
   * y ese mismo valor es el que sale en el `Par[]` del PDF. Lo que hubiera guardado en `valores[clave]` se ignora.
   */
  readonly calculado?: (valores: Valores) => string;
  /** Solo lectura sin fórmula propia: el valor lo aporta quien renderiza (p. ej. un total que sale de otra tabla). */
  readonly soloLectura?: boolean;
  /** Límites del `<input type=number>` (paso 1): solo ayudan al teclado; la validación real la hace quien guarda. */
  readonly min?: number;
  readonly max?: number;
}

export const OPCIONES_SI_NO: readonly OpcionCampo[] = [
  { valor: 'si', etiqueta: 'Sí' },
  { valor: 'no', etiqueta: 'No' },
];
/** N/C = "no conoce" (leyenda impresa en 052). */
export const OPCIONES_SI_NO_NC: readonly OpcionCampo[] = [...OPCIONES_SI_NO, { valor: 'nc', etiqueta: 'N/C (no conoce)' }];
/** NA = no aplica (instructivo de 057: "en caso de que existan variables que no pueden ser llenadas, se colocará NA"). */
export const OPCIONES_SI_NO_NA: readonly OpcionCampo[] = [...OPCIONES_SI_NO, { valor: 'na', etiqueta: 'NA' }];
export const OPCIONES_NORMAL_ANORMAL: readonly OpcionCampo[] = [
  { valor: 'normal', etiqueta: 'Normal' },
  { valor: 'anormal', etiqueta: 'Anormal' },
];

/**
 * CASILLA TRI-ESTADO (Sí / No / sin responder).
 *
 * Criterio: cuando el papel imprime un par SI / NO por pregunta, "No" y "no se contestó" son hechos distintos y el PDF
 * firmado debe distinguirlos. Un `boolean` (o la casilla `'true'`/`''`) no puede: `false` = "No" o "no respondió", y el
 * patrón `v ? 'Sí' : ''` + filtrar respuestas vacías borraba el "No" del documento. Por eso esas preguntas se guardan como
 * `'si' | 'no' | ''` y se emiten como 'Sí' / 'No' / (nada).
 *
 * NO aplica a casillas de "marcar / no marcar" (un solo recuadro en el papel: ahí no marcar no es contestar "No").
 *
 * Compatibilidad hacia atrás: los estados que aún traen booleanos se leen con `leerSiNo`
 * (`true`/`'true'` -> 'si'; `false`/`undefined`/`''` -> sin responder: un `false` viejo nunca fue un "No" verificable).
 */
export type SiNoValor = 'si' | 'no' | '';

/** Normaliza cualquier valor guardado (tri-estado, booleano viejo, `'true'`) a `SiNoValor`. */
export function leerSiNo(v: unknown): SiNoValor {
  if (v === 'si' || v === 'no') return v;
  if (v === true || v === 'true') return 'si';
  return '';
}

/** 'Sí' / 'No' / '' — lo que va al `Par[]` (los `Par` con respuesta vacía los descarta quien arma el PDF). */
export function textoSiNo(v: unknown, textos: { readonly si: string; readonly no: string } = { si: 'Sí', no: 'No' }): string {
  const n = leerSiNo(v);
  return n === 'si' ? textos.si : n === 'no' ? textos.no : '';
}

/** Texto a mostrar/imprimir de un campo ya cargado — etiqueta de la opción (`select`), "Sí" (`casilla`) o el valor tal cual. */
export function textoDeCampo(campo: CampoSimple, valor: string | undefined): string {
  const v = (valor ?? '').trim();
  if (v === '') return '';
  if (campo.tipo === 'select') return campo.opciones?.find((o) => o.valor === v)?.etiqueta ?? v;
  if (campo.tipo === 'casilla') return v === 'true' ? 'Sí' : '';
  if (campo.tipo === 'siNo') return textoSiNo(v);
  if (campo.tipo === 'numeroNA' && v.toUpperCase() === 'NA') return 'NA';
  return v;
}

export type Valores = Readonly<Record<string, string>>;

export function valoresVacios(campos: readonly CampoSimple[]): Record<string, string> {
  return Object.fromEntries(campos.map((c) => [c.clave, '']));
}

/** Un `Par` por campo (con `prefijo` opcional en la pregunta) — el filtrado de vacíos lo hace quien arma el `Par[]` final. */
export function paresDeCampos(campos: readonly CampoSimple[], valores: Valores, prefijo = ''): Par[] {
  return campos.map((c) => ({ pregunta: `${prefijo}${c.etiqueta}`, respuesta: c.calculado ? c.calculado(valores) : textoDeCampo(c, valores[c.clave]) }));
}

/** PRE = presuntivo, DEF = definitivo — marca de los diagnósticos del Anexo 1. */
export const MARCAS_PRE_DEF: readonly OpcionMarca[] = [
  { valor: 'pre', texto: 'PRE' },
  { valor: 'def', texto: 'DEF' },
];

/** "texto (CIE)" — el papel tiene columna CIE junto a cada diagnóstico; mismo formato que 012A/013A/117. */
export function conCodigoCie(d: DiagnosticoCie10Valor): string {
  const texto = d.texto.trim();
  return texto && d.cie10 ? `${texto} (${d.cie10.code})` : texto;
}

/**
 * "Describir anotando el número": al marcar la casilla N de un bloque con "Describa" (`<prefijo>Describa`), agrega al final
 * del Describa el renglón `N. ` para que la descripción quede ligada al ítem. No toca lo ya escrito ni repite un número que
 * ya tiene renglón. Solo reacciona a casillas que pasan de sin marcar a marcada.
 */
export function vincularDescriba(anterior: Valores, nuevo: Valores, claveDescriba: string): Valores {
  const prefijo = claveDescriba.replace(/Describa$/, '');
  const patron = new RegExp(`^${prefijo}([0-9]+)$`);
  let texto = nuevo[claveDescriba] ?? '';
  let cambio = false;
  const marcadas = Object.keys(nuevo)
    .map((k) => patron.exec(k))
    .filter((m): m is RegExpExecArray => m !== null && nuevo[m[0]] === 'true' && anterior[m[0]] !== 'true')
    .map((m) => Number(m[1]))
    .sort((a, b) => a - b);
  for (const n of marcadas) {
    // El separador debe ir seguido de espacio o fin de línea: "1.5 L" o "3-4 veces" NO son el renglón "1." / "3.".
    if (new RegExp(`^[ \t]*${n}[ \t]*[.:)-](?:[ \t]|$)`, 'm').test(texto)) continue;
    texto = texto === '' || texto.endsWith('\n') ? `${texto}${n}. ` : `${texto}\n${n}. `;
    cambio = true;
  }
  return cambio ? { ...nuevo, [claveDescriba]: texto } : nuevo;
}

/** Quita del texto los renglones que solo traen el número ("3." sin descripción) — no deben salir en el PDF firmado. */
export function sinRenglonesVacios(texto: string): string {
  return texto
    .split('\n')
    .filter((l) => !/^[ \t]*[0-9]+[ \t]*[.:)-][ \t]*$/.test(l))
    .join('\n')
    .trim();
}

/** Etiquetas de los campos `numeroNA` con un valor que no es un número ni "NA" (vacío = sin llenar, válido). */
export function numeroNAInvalidos(campos: readonly CampoSimple[], valores: Valores): string[] {
  return campos
    .filter((c) => c.tipo === 'numeroNA' && !c.calculado)
    .filter((c) => {
      const v = (valores[c.clave] ?? '').trim();
      return v !== '' && v.toUpperCase() !== 'NA' && !/^[0-9]+([.,][0-9]+)?$/.test(v);
    })
    .map((c) => c.etiqueta);
}
